import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const BUSINESS_CATEGORIES = [
  { value: 'fashion', label: 'Fashion & Apparel', icon: 'Shirt' },
  { value: 'tech', label: 'Technology', icon: 'Smartphone' },
  { value: 'food', label: 'Food & Beverage', icon: 'UtensilsCrossed' },
  { value: 'beauty', label: 'Beauty & Cosmetics', icon: 'Sparkles' },
  { value: 'health', label: 'Health & Wellness', icon: 'Heart' },
  { value: 'travel', label: 'Travel & Hospitality', icon: 'Plane' },
  { value: 'finance', label: 'Finance & Fintech', icon: 'TrendingUp' },
  { value: 'retail', label: 'Retail & E-commerce', icon: 'ShoppingBag' },
  { value: 'entertainment', label: 'Entertainment', icon: 'Play' },
  { value: 'education', label: 'Education', icon: 'BookOpen' },
  { value: 'sports', label: 'Sports & Fitness', icon: 'Dumbbell' },
  { value: 'other', label: 'Other', icon: 'MoreHorizontal' },
];

const BUDGET_RANGES = [
  { value: 'under-1k', label: 'Under $1K/mo' },
  { value: '1k-5k', label: '$1K – $5K/mo' },
  { value: '5k-20k', label: '$5K – $20K/mo' },
  { value: '20k-50k', label: '$20K – $50K/mo' },
  { value: '50k-100k', label: '$50K – $100K/mo' },
  { value: '100k+', label: '$100K+/mo' },
];

const BrandVerificationForm = ({ formData, onChange, errors }) => {
  const [verifyingWebsite, setVerifyingWebsite] = useState(false);
  const [websiteVerified, setWebsiteVerified] = useState(false);

  const handleWebsiteVerify = async () => {
    if (!formData?.websiteUrl?.trim()) return;
    setVerifyingWebsite(true);
    await new Promise(r => setTimeout(r, 1500));
    setVerifyingWebsite(false);
    setWebsiteVerified(true);
  };

  const handleWebsiteChange = (value) => {
    onChange('websiteUrl', value);
    if (websiteVerified) setWebsiteVerified(false);
  };

  return (
    <div className="space-y-8">
      {/* Brand Name */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Building2" size={18} color="var(--color-primary)" />
          <h3 className="text-base font-semibold text-[var(--color-foreground)]">Company Profile</h3>
          <span className="text-xs text-[var(--color-error)] font-medium">*Required</span>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[var(--color-foreground)] mb-2">
              Brand / Company Name <span className="text-[var(--color-error)]">*</span>
            </label>
            {errors?.brandName && (
              <p className="text-xs text-[var(--color-error)] mb-2">{errors?.brandName}</p>
            )}
            <input
              type="text"
              placeholder="e.g. Acme Corporation"
              value={formData?.brandName || ''}
              onChange={e => onChange('brandName', e?.target?.value)}
              className={`w-full h-11 px-4 rounded-xl bg-[var(--color-background)] border text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/50 transition-colors ${
                errors?.brandName ? 'border-[var(--color-error)]' : 'border-[var(--color-border)]'
              }`}
            />
          </div>

          {/* Website URL */}
          <div>
            <label className="block text-sm font-medium text-[var(--color-foreground)] mb-2">
              Official Website <span className="text-[var(--color-error)]">*</span>
            </label>
            {errors?.websiteUrl && (
              <p className="text-xs text-[var(--color-error)] mb-2">{errors?.websiteUrl}</p>
            )}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--color-muted-foreground)] text-sm">https://</span>
                <input
                  type="text"
                  placeholder="yourbrand.com"
                  value={formData?.websiteUrl || ''}
                  onChange={e => handleWebsiteChange(e?.target?.value)}
                  className={`w-full h-11 pl-16 pr-4 rounded-xl bg-[var(--color-background)] border text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/50 transition-colors ${
                    errors?.websiteUrl ? 'border-[var(--color-error)]' : 'border-[var(--color-border)]'
                  }`}
                />
              </div>
              <button
                type="button"
                onClick={handleWebsiteVerify}
                disabled={!formData?.websiteUrl?.trim() || verifyingWebsite}
                className="px-4 h-11 rounded-xl text-sm font-medium transition-all duration-200 flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{
                  background: websiteVerified
                    ? 'var(--color-success)'
                    : 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))',
                  color: '#fff'
                }}
              >
                {verifyingWebsite ? (
                  <><Icon name="Loader" size={14} className="animate-spin" /> Checking...</>
                ) : websiteVerified ? (
                  <><Icon name="CheckCircle" size={14} /> Verified</>
                ) : (
                  'Verify'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Business Category */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Icon name="Tag" size={18} color="var(--color-secondary)" />
          <h3 className="text-base font-semibold text-[var(--color-foreground)]">Business Category</h3>
          <span className="text-xs text-[var(--color-error)] font-medium">*Required</span>
        </div>
        {errors?.businessCategory && (
          <p className="text-xs text-[var(--color-error)] mb-3">{errors?.businessCategory}</p>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {BUSINESS_CATEGORIES?.map(cat => (
            <button
              key={cat?.value}
              type="button"
              onClick={() => onChange('businessCategory', cat?.value)}
              className={`flex items-center gap-2 px-3 py-3 rounded-xl text-sm font-medium transition-all duration-200 text-left ${
                formData?.businessCategory === cat?.value
                  ? 'bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] text-white shadow-md'
                  : 'bg-[var(--color-card)] text-[var(--color-foreground)] hover:bg-[var(--color-muted)] border border-[var(--color-border)]'
              }`}
            >
              <Icon
                name={cat?.icon}
                size={14}
                color={formData?.businessCategory === cat?.value ? '#FFFFFF' : 'var(--color-primary)'}
              />
              <span className="text-xs">{cat?.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Campaign Budget */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Icon name="DollarSign" size={18} color="var(--color-accent)" />
          <h3 className="text-base font-semibold text-[var(--color-foreground)]">Campaign Budget Preference</h3>
          <span className="text-xs text-[var(--color-error)] font-medium">*Required</span>
        </div>
        {errors?.campaignBudget && (
          <p className="text-xs text-[var(--color-error)] mb-3">{errors?.campaignBudget}</p>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {BUDGET_RANGES?.map(range => (
            <button
              key={range?.value}
              type="button"
              onClick={() => onChange('campaignBudget', range?.value)}
              className={`py-3 px-4 rounded-xl text-sm font-medium transition-all duration-200 ${
                formData?.campaignBudget === range?.value
                  ? 'bg-gradient-to-br from-[var(--color-accent)] to-[var(--color-primary)] text-white shadow-md'
                  : 'bg-[var(--color-card)] text-[var(--color-foreground)] hover:bg-[var(--color-muted)] border border-[var(--color-border)]'
              }`}
            >
              {range?.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BrandVerificationForm;
